import { TemplateResult, html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { SlotController } from '../../controllers/slot-controller';
import { BlocksElement } from '../BlocksElement';
import '../icon/icons/arrow-back';
import type { LinkTarget } from './breadcrumbs-item.model';
import styles from './breadcrumbs-item.styles';

/**
 * @slot before - The component before content
 * @slot default - The component content
 * @slot after - The component after content
 * @slot separator - The component separator
 * @slot custom-link - The component's custom link slot
 * @slot showmore - The component showmore content
 * @csspart base - The component's base wrapper.
 * @csspart mobile-action - The component's wrapper for mobile action.
 * @csspart icon - The component's icon `cat-icon-arrow-back`.
 * @csspart text - The component's text.
 * @csspart part-default - The component's default slot.
 * @csspart separator-wrapper - The component's separator wrapper.
 * @csspart part-separator - The component's separator.
 * @csspart default-separator - The component's default-separator.
 * @csspart link - The component's link element.
 * @csspart before-wrapper - The component's before wrapper.
 * @csspart part-before - The component's before slot.
 * @csspart link-text - The component's link-text.
 * @csspart after-wrapper - The component's after wrapper.
 * @csspart part-after - The component's after slot.
 * @csspart label-button - The component's label-button wrapper.
 * @csspart show-more-wrapper - The component's show-more wrapper.
 * @csspart show-more - The component's show-more slot.
 * @csspart separator-wrapper - The component's separator wrapper.
 * @csspart separator - The component's separator slot.
 * @csspart default-separator - The component's default-separator.
 * @csspart part-custom-link - The component's custom link slot 

 */
export class BlocksBreadcrumbsItem extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Item link URL
   */
  @property({ type: String })
  href: string | undefined;

  /**
   * Loading state
   */
  @property({ type: Boolean })
  isLoading: boolean | undefined;

  /**
   * Target attribute
   * 1) Default target uses the same tab when the link is clicked
   * <cat-text-passage size="sm">
   * <ul>
   * <li>**_blank** opens a link in a new window/tab</li>
   * <li>**_self** opens a link in the same tab/frame </li>
   * <li>**_parent** opens the link in a parent frame </li>
   * <li>**_top** opens the link in the full body of the window</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  target: LinkTarget;

  /**
   * Hide separator icon based on this flag
   */
  @property({ type: Boolean })
  isLast: boolean | undefined;

  /**
   * Mobile Variant
   */
  @property({ type: Boolean })
  isMobile: boolean | undefined;

  /**
   * Active state
   */
  @property({ type: Boolean, reflect: true })
  isActive: boolean | undefined;

  /**
   * prevent click on separator icon
   * @param event Event
   */
  private stopEventPropagation(event: Event): void {
    event.stopPropagation();
  }

  protected slots = new SlotController(this, null, 'before', 'after', 'separator', 'custom-link', 'showmore');

  private get hasBeforeSlot(): boolean {
    return this.slots.hasSlotted('before');
  }
  private get hasAfterSlot(): boolean {
    return this.slots.hasSlotted('after');
  }
  private get hasCustomLinkSlot(): boolean {
    return this.slots.hasSlotted('custom-link');
  }
  private get hasShowMoreSlot(): boolean {
    return this.slots.hasSlotted('showmore');
  }

  render(): TemplateResult<1> {
    const componentClassNames = this.componentClassNames('cat-c-breadcrumbs__item', {
      'cat-c-breadcrumbs__item--mobile': this.isMobile === true,
      'cat-c-breadcrumbs__item--active': this.isActive,
      'cat-c-breadcrumbs--has-before': this.hasBeforeSlot,
      'cat-c-breadcrumbs--has-after': this.hasAfterSlot,
      'cat-c-breadcrumbs--has-showmore': this.hasShowMoreSlot,
      'cat-c-breadcrumbs--not-is-last': !this.isLast,
      'cat-c-breadcrumbs--has-no-showmore': !this.href && !this.hasCustomLinkSlot && !this.hasShowMoreSlot
    });

    return html`
      <div part="base" class="${componentClassNames}">
        ${this.isMobile
          ? html`
              <div part="mobile-action" class="cat-c-breadcrumbs__item--mobile--action" tabindex="0" role=${this.href ? 'link' : 'button'}>
                <cat-icon-arrow-back part="icon" exportparts=${this.getExportParts('arrow-back', 'icon')}></cat-icon-arrow-back>
                <span part="text" class="cat-c-breadcrumbs__item--text">
                  <slot part="part-default"></slot>
                </span>
              </div>
              <div
                part="separator-wrapper"
                class="cat-c-breadcrumbs__separator breadcrumbs-separator-mobile"
                inert="${true}"
                @click=${this.stopEventPropagation}
              >
                <slot part="part-separator" name="separator">
                  <span part="default-separator" class="default-seperator">/</span>
                </slot>
              </div>
            `
          : html` ${this.href
                ? html`
                    <a
                      part="link"
                      class="cat-c-breadcrumbs__link"
                      href=${ifDefined(this.href)}
                      target=${ifDefined(this.target)}
                      aria-current=${ifDefined(this.isActive ? 'page' : undefined)}
                    >
                      <div part="before-wrapper" class="cat-c-breadcrumbs__before">
                        <slot part="part-before" name="before"></slot>
                      </div>
                      <span part="link-text" class="cat-c-breadcrumbs__link--text">
                        <slot part="part-default"></slot>
                      </span>
                      <div part="after-wrapper" class="cat-c-breadcrumbs__after">
                        <slot part="part-after" name="after"></slot>
                      </div>
                    </a>
                  `
                : this.hasCustomLinkSlot
                  ? html` <div part="link" class="cat-c-breadcrumbs__link">
                      <span part="link-text" class="cat-c-breadcrumbs__link--text">
                        <slot part="part-custom-link" name="custom-link"></slot>
                      </span>
                    </div>`
                  : ''}

              <button
                part="label-button"
                type="button"
                class="cat-c-breadcrumbs__item--label--button"
                tabindex=${ifDefined(this.isLoading ? '-1' : undefined)}
                aria-current=${ifDefined(this.isActive ? 'page' : undefined)}
              >
                <div part="before-wrapper" class="cat-c-breadcrumbs__before">
                  <slot part="part-before" name="before"></slot>
                </div>
                <slot part="part-default"></slot>
                <div part="after-wrapper" class="cat-c-breadcrumbs__after">
                  <slot part="part-after" name="after"></slot>
                </div>
              </button>
              <div part="show-more-wrapper" class="cat-c-breadcrumbs__showmore">
                <slot part="show-more" name="showmore"></slot>
              </div>
              <div
                part="separator-wrapper"
                class="cat-c-breadcrumbs__separator breadcrumbs-separator-lg"
                aria-hidden="true"
                @click=${this.stopEventPropagation}
              >
                <slot part="part-separator" name="separator">
                  <span part="default-separator" class="default-seperator">/</span>
                </slot>
              </div>`}
      </div>
    `;
  }
}

if (customElements.get('cat-breadcrumbs-item') === undefined) {
  customElements.define('cat-breadcrumbs-item', BlocksBreadcrumbsItem);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-breadcrumbs-item': BlocksBreadcrumbsItem;
  }
}
